/**
 * The MoveReport, rendered — one implementation shared by every surface that commits
 * hierarchy moves. Callers hold it out-of-band from the draft, so a no-op move still
 * explains itself even though the commit records nothing.
 */
import { Flex, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import type { MoveReport } from "../domain/hierarchyMoves/applyHierarchyMove";
import { lengthLabel, type LengthUnit } from "../domain/studioTypes";
import { STUDIO_THEME } from "../constants/studioTheme";
import {
  failedGatesOf,
  TOP_REASONS,
  useAestheticsWording,
} from "src/components/PlanogramExtensions/aestheticsWording";

interface Props {
  report: MoveReport | null;
  /** The category's unit, as the panel resolved it off the release. */
  lengthUnit: LengthUnit;
  /**
   * Keep only the neutral mechanism line. For the vertical/horizontal ambiguity answers
   * ONLY — level and sibling moves spend the plan's facings and keep every line.
   */
  quiet?: boolean;
}

const scoreOf = (value: number): string | number => (Number.isNaN(value) ? "—" : Math.round(value));

export const MoveReportSummary = ({ report, lengthUnit, quiet = false }: Props) => {
  const { t } = useTranslation("cdtStudio");
  const wording = useAestheticsWording();
  if (!report) return null;
  const afterWhy = report.committed ? (report.aesthetics?.afterWhy ?? null) : null;
  const failedGates = afterWhy ? failedGatesOf(afterWhy.gates) : [];
  return (
    <Flex column gap="2px" padding="4px 12px" data-testid="cdt-studio-move-report">
      <Text variant="small2" color={STUDIO_THEME.textSecondary}>
        {!report.committed
          ? t("moveReport.noop")
          : report.mechanism === "slide"
            ? t("moveReport.mechanismSlide")
            : report.mechanism === "reband"
              ? t("moveReport.mechanismReband")
              : report.mechanism === "remeasure"
                ? t("moveReport.mechanismRemeasure")
                : report.mechanism === "branch-rebuild"
                  ? t("moveReport.mechanismBranchRebuild")
                  : report.mechanism === "rebuild"
                    ? t("moveReport.mechanismRebuild")
                    : report.surgical
                      ? t("moveReport.surgical")
                      : t("moveReport.replay")}
      </Text>
      {quiet ? null : (
        <>
          {report.committed && report.churn && (
            <Text variant="small2" color={STUDIO_THEME.textSecondary}>
              {report.churn.movedProducts === 0 && report.churn.facingsRedealt === 0
                ? t("moveReport.churnNone")
                : t("moveReport.churn", {
                    moved: report.churn.movedProducts,
                    total: report.churn.totalProducts,
                    facings: report.churn.facingsRedealt,
                  })}
            </Text>
          )}
          {report.committed && report.aesthetics && (
            <Text variant="small2" color={STUDIO_THEME.textSecondary}>
              {t("moveReport.aesthetics", {
                before: scoreOf(report.aesthetics.before),
                after: scoreOf(report.aesthetics.after),
              })}
            </Text>
          )}
          {afterWhy && afterWhy.penalties.length > 0 && (
            <Flex column data-testid="cdt-studio-move-report-reasons">
              <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                {wording.reasonsTitle}
              </Text>
              {afterWhy.penalties.slice(0, TOP_REASONS).map((reason) => (
                <Text key={reason.code} variant="small2" color={STUDIO_THEME.textSecondary}>
                  {wording.reason(reason)}
                </Text>
              ))}
            </Flex>
          )}
          {failedGates.length > 0 && (
            <Flex column data-testid="cdt-studio-move-report-gates">
              <Text variant="small2" color={STUDIO_THEME.severity.toCheck.text}>
                {wording.gatesTitle}
              </Text>
              {failedGates.map((gate) => (
                <Text key={gate.id} variant="small2" color={STUDIO_THEME.severity.toCheck.text}>
                  {wording.gate(gate)}
                </Text>
              ))}
            </Flex>
          )}
          {report.committed && report.fill && (
            <Text variant="small2" color={STUDIO_THEME.textSecondary}>
              {t("moveReport.fill", {
                before: report.fill.before,
                after: report.fill.after,
              })}
              {report.fill.worstRun &&
                report.fill.worstRun.after !== report.fill.after &&
                ` ${t("moveReport.fillWorstRun", {
                  run: report.fill.worstRun.run + 1,
                  before: report.fill.worstRun.before,
                  after: report.fill.worstRun.after,
                })}`}
              {report.fill.worstRun &&
                report.fill.worstRun.overflowCm > 0 &&
                ` ${t("moveReport.fillOverflow", { length: lengthLabel(report.fill.worstRun.overflowCm, lengthUnit) })}`}
            </Text>
          )}
          {report.committed && report.expectedSales && (
            <Text variant="small2" color={STUDIO_THEME.textSecondary}>
              {report.expectedSales.after === report.expectedSales.before
                ? t("moveReport.expectedSales")
                : t("moveReport.expectedSalesDelta", {
                    before: report.expectedSales.before,
                    after: report.expectedSales.after,
                  })}
            </Text>
          )}
          {report.concessions.map((concession) => (
            <Text
              key={`${concession.kind}:${concession.detail}`}
              variant="small2"
              color={STUDIO_THEME.severity.toCheck.text}
            >
              {concession.detail}
            </Text>
          ))}
        </>
      )}
    </Flex>
  );
};
